import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  CardActionArea,
  Radio,
  Card,
  RadioGroup,
  Typography,
  FormControl,
  FormLabel,
  Divider,
  FormControlLabel,
  TextField,
  Checkbox,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";

import DateField from "../../../../../../shared/Form/DateField";

import { CreateServiceFormData } from "../../../../types/service.type";
import { useMinimumServicePrices } from "../../../../../../api/queries/payment/paymentQueries";
import { currencyList, ServiceRequestTypeEnum, serviceRequestUnitList } from "../../../../enum/service.enum";
import { FinalServiceCostIcon, ServiceAmountWarningIcon } from "../../../../assets/icons/create-service-icons";
import { TermsAndConditionsModal, useTermsAndConditionsModal } from "../../../../components/TermsAndConditionsModal";

const PricingAndTerms = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.pricingAndTermsStep" });
  const { t: tCurrency } = useTranslation("common", { keyPrefix: "currency" });
  const { open, handleOpen, handleClose } = useTermsAndConditionsModal();
  const {
    control,
    formState: { errors },
    clearErrors,
    watch,
  } = useFormContext<CreateServiceFormData>();

  const paymentConfigType = watch("paymentConfig.type");
  const currency = watch("paymentConfig.currency");
  const amount = watch("paymentConfig.amount");
  const isNotExpired = watch("paymentConfig.notExpired");
  const amountNumber = Number(amount);

  const { minimumServicePrices } = useMinimumServicePrices();
  const minimumServicePrice = minimumServicePrices?.[currency as keyof typeof minimumServicePrices];

  const currencyName = tCurrency(currency);
  return (
    <Box sx={styles.container}>
      <Controller
        name="paymentConfig.type"
        control={control}
        render={({ field }) => (
          <FormControl error={!!errors.paymentConfig?.type}>
            <FormLabel sx={styles.formLabel}>{t("pricingAndPayment")}</FormLabel>

            <RadioGroup
              sx={styles.serviceDeliveryRadioGroup}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
            >
              <Card
                sx={styles.pricingOptionCard(field.value === ServiceRequestTypeEnum.Unit)}
                onClick={() => {
                  field.onChange(ServiceRequestTypeEnum.Unit);
                  clearErrors(["paymentConfig.unit", "paymentConfig.amount"]);
                }}
              >
                <Radio
                  size="small"
                  value={ServiceRequestTypeEnum.Unit}
                  checked={field.value === ServiceRequestTypeEnum.Unit}
                />
                <CardActionArea sx={styles.serviceDeliveryCardActionArea} disableRipple>
                  <Box sx={styles.serviceDeliveryCardTitleBox}>
                    <Typography sx={styles.serviceDeliveryCardTitle}>{t("unitCost")}</Typography>
                    <Typography sx={styles.serviceDeliveryCardDescription}>{t("costPerUnitYouWillChoose")}</Typography>
                  </Box>
                  {paymentConfigType === ServiceRequestTypeEnum.Unit && (
                    <>
                      <Box sx={styles.serviceAmountWarningBox}>
                        <ServiceAmountWarningIcon />
                        <Typography sx={styles.serviceDeliveryCardDescription}>
                          {t("serviceAmountWarning", { currency: currencyName, amount: minimumServicePrice })}
                        </Typography>
                      </Box>
                      <Box sx={styles.unitCostSubContainer}>
                        <Box sx={styles.unitCostSubItem}>
                          <Controller
                            name="paymentConfig.amount"
                            control={control}
                            render={({ field: amountField }) => (
                              <>
                                <FormLabel sx={styles.formChildLabel} error={!!errors.paymentConfig?.amount}>
                                  {t("pricePerServiceUnit")}
                                </FormLabel>
                                <Box sx={styles.currencySelectSubMenuContainer}>
                                  <Controller
                                    name="paymentConfig.currency"
                                    control={control}
                                    render={({ field: currencyField }) => (
                                      <Select
                                        {...currencyField}
                                        value={currencyField.value}
                                        sx={styles.currencySelectSubMenu}
                                        onChange={(e) => currencyField.onChange(e.target.value)}
                                      >
                                        {currencyList.map((currency: string) => {
                                          return (
                                            <MenuItem key={currency} value={currency}>
                                              {currency}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    )}
                                  />
                                  <TextField
                                    {...amountField}
                                    fullWidth
                                    placeholder={t("averageCost")}
                                    sx={styles.averageCostTextField}
                                  />
                                </Box>
                                {errors.paymentConfig?.amount && (
                                  <FormHelperText error>
                                    {t(errors.paymentConfig?.amount?.message, { amount: minimumServicePrice })}
                                  </FormHelperText>
                                )}
                              </>
                            )}
                          />
                        </Box>
                        <Box sx={styles.unitCostSubItem}>
                          <FormLabel sx={styles.formChildLabel} error={!!errors.paymentConfig?.unit}>
                            {t("theServiceUnit")}
                          </FormLabel>
                          <Controller
                            name="paymentConfig.unit"
                            control={control}
                            render={({ field: unitField }) => (
                              <>
                                <Select
                                  {...unitField}
                                  displayEmpty
                                  fullWidth
                                  value={unitField.value}
                                  onChange={(e) => unitField.onChange(e.target.value)}
                                  error={!!errors.paymentConfig?.unit}
                                >
                                  <MenuItem selected value="" disabled>
                                    {t("selectTheServiceUnitPerRate")}
                                  </MenuItem>
                                  {serviceRequestUnitList.map((unit, idx) => (
                                    <MenuItem key={idx} value={unit}>
                                      {t(unit)}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors.paymentConfig?.unit && (
                                  <FormHelperText error>{t(errors.paymentConfig?.unit?.message)}</FormHelperText>
                                )}
                              </>
                            )}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </CardActionArea>
              </Card>
              <Card
                sx={styles.pricingOptionCard(field.value === ServiceRequestTypeEnum.Fixed)}
                onClick={() => {
                  field.onChange(ServiceRequestTypeEnum.Fixed);
                  clearErrors(["paymentConfig.unit", "paymentConfig.amount"]);
                }}
              >
                <Radio
                  size="small"
                  value={ServiceRequestTypeEnum.Fixed}
                  checked={field.value === ServiceRequestTypeEnum.Fixed}
                />
                <CardActionArea sx={styles.serviceDeliveryCardActionArea} disableRipple>
                  <Box sx={styles.serviceDeliveryCardTitleBox}>
                    <Typography sx={styles.serviceDeliveryCardTitle}>{t("fixedCost")}</Typography>
                    <Typography sx={styles.serviceDeliveryCardDescription}>
                      {t("setAFixedAndTotalPriceForYourRequest")}
                    </Typography>
                  </Box>
                  {paymentConfigType === ServiceRequestTypeEnum.Fixed && (
                    <>
                      <Box sx={styles.serviceAmountWarningBox}>
                        <ServiceAmountWarningIcon />
                        <Typography sx={styles.serviceDeliveryCardDescription}>
                          {t("serviceAmountWarning", { currency: currencyName, amount: minimumServicePrice })}
                        </Typography>
                      </Box>
                      <Box sx={styles.unitCostSubContainer}>
                        <Box sx={styles.unitCostSubItem}>
                          <Controller
                            name="paymentConfig.amount"
                            control={control}
                            render={({ field: amountField }) => (
                              <>
                                <FormLabel sx={styles.formChildLabel} error={!!errors.paymentConfig?.amount}>
                                  {t("totalPrice")}
                                </FormLabel>
                                <Box sx={styles.currencySelectSubMenuContainer}>
                                  <Controller
                                    name="paymentConfig.currency"
                                    control={control}
                                    render={({ field: currencyField }) => (
                                      <Select
                                        {...currencyField}
                                        value={currencyField.value}
                                        sx={styles.currencySelectSubMenu}
                                        onChange={(e) => currencyField.onChange(e.target.value)}
                                      >
                                        {currencyList.map((currency: string) => {
                                          return (
                                            <MenuItem key={currency} value={currency}>
                                              {currency}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    )}
                                  />
                                  <TextField
                                    {...amountField}
                                    error={!!errors.paymentConfig?.amount}
                                    fullWidth
                                    placeholder={t("enterTotalPrice")}
                                    sx={styles.averageCostTextField}
                                  />
                                </Box>
                                {errors.paymentConfig?.amount && (
                                  <FormHelperText error>
                                    {t(errors.paymentConfig?.amount?.message, { amount: minimumServicePrice })}
                                  </FormHelperText>
                                )}
                              </>
                            )}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </CardActionArea>
              </Card>
            </RadioGroup>
            {errors?.paymentConfig?.type && (
              <FormHelperText error>{t(errors.paymentConfig?.amount?.message as string)}</FormHelperText>
            )}
          </FormControl>
        )}
      />
      <Divider />
      <Box sx={styles.finalServiceCostContainer}>
        <FinalServiceCostIcon />
        <Box sx={styles.finalServiceCostBox}>
          <Typography sx={styles.finalServiceCostTitle}>{t("yourFinalServiceCost")}</Typography>
          <Typography sx={styles.finalServiceCostDescription}>{t("goalngoService")}</Typography>
        </Box>
        <Box sx={styles.finalServiceCostRow}>
          <Typography sx={styles.serviceCostTitle}>{t("servicePriceForUsers")}</Typography>
          <Typography sx={styles.finalServiceCostValue}>
            {currency} {amountNumber ? amountNumber.toFixed(2) : "00.00"}
          </Typography>
        </Box>
        <Box sx={styles.finalServiceCostRow}>
          <Typography sx={styles.serviceCostTitle}>{t("fees10")}</Typography>
          <Typography sx={styles.finalServiceCostValue}>
            {currency} {amountNumber ? (amountNumber * 0.1).toFixed(2) : "00.00"}
          </Typography>
        </Box>
        <Divider />
        <Box sx={styles.finalServiceCostRow}>
          <Typography sx={styles.serviceCostTitle}>{t("totalPrice")}</Typography>
          <Typography sx={styles.finalServiceCostValue}>
            {currency} {amountNumber ? (amountNumber * 0.9).toFixed(2) : "00.00"}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Controller
        name="paymentConfig.expiryDate"
        control={control}
        render={({ field }) => (
          <FormControl>
            <Typography sx={styles.formLabel}>{t("serviceValidity")}</Typography>
            {!isNotExpired && (
              <>
                <FormLabel sx={styles.formChildLabel} error={!!errors.paymentConfig?.expiryDate}>
                  {t("serviceExpireDate")}
                </FormLabel>
                <DateField
                  placeholder={t("selectEndDate")}
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => field.onChange(date?.toISOString())}
                  error={!!errors.paymentConfig?.expiryDate}
                  disablePast
                />
                {errors.paymentConfig?.expiryDate && (
                  <FormHelperText error>{t(errors.paymentConfig?.expiryDate?.message)}</FormHelperText>
                )}
              </>
            )}
            <Controller
              name="paymentConfig.notExpired"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={styles.formControlLabel}
                  control={
                    <Checkbox
                      size="small"
                      checked={field.value ?? false}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        clearErrors("paymentConfig.expiryDate");
                      }}
                    />
                  }
                  label={t("thisDealDoesNotExpire")}
                />
              )}
            />
          </FormControl>
        )}
      />
      <Divider />
      <Typography sx={styles.formLabel}>{t("termsAndCondetion")}</Typography>
      <Controller
        name="paymentConfig.allowGoalngoMemebers"
        control={control}
        render={({ field }) => (
          <>
            <FormControlLabel
              sx={styles.formControlLabel}
              control={
                <Checkbox
                  size="small"
                  checked={field.value ?? false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={
                <FormLabel sx={styles.formControlLabel} error={!!errors.paymentConfig?.allowGoalngoMemebers}>
                  {t("allowGoalngoMemebers")}
                </FormLabel>
              }
            />
          </>
        )}
      />
      <Controller
        name="paymentConfig.agreeToTerms"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            sx={styles.formControlLabel}
            control={
              <Checkbox
                size="small"
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label={
              <FormLabel
                sx={{ ...styles.formControlLabel, display: "flex", gap: "4px" }}
                error={!!errors.paymentConfig?.allowGoalngoMemebers}
              >
                {t("iAgreeToGoalngo")}
                <Typography
                  sx={{ ...styles.formControlLabel, color: "#106048", textDecoration: "underline", cursor: "pointer" }}
                  onClick={handleOpen}
                >
                  {t("termsAndConditions")}
                </Typography>
              </FormLabel>
            }
          />
        )}
      />
      <TermsAndConditionsModal open={open} closeModal={handleClose} />
    </Box>
  );
};

export default PricingAndTerms;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceDeliveryRadioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  pricingOptionCard: (isSelected: boolean) => ({
    flex: 1,
    border: isSelected ? "1px solid var(--Green---100, #008B07)" : "1px solid var(--Gray-200, #E4E7EC)",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  }),
  serviceDeliveryCardActionArea: {
    display: "flex",
    flexDirection: " column",
    alignItems: "flex-start",
    gap: "24px",
    "& .MuiCardActionArea-focusHighlight": {
      backgroundColor: "transparent !important",
    },
  },
  serviceDeliveryCardTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  serviceDeliveryCardDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  formControlLabel: {
    margin: 0,
    gap: "12px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  formChildLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  finalServiceCostContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--Green---25, #E7EFED)",
    padding: "16px",
  },
  finalServiceCostBox: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  finalServiceCostRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  finalServiceCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  finalServiceCostDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  serviceCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  finalServiceCostValue: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  serviceDeliveryCardTitleBox: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  serviceAmountWarningBox: {
    display: "flex",
    gap: "4px",
    borderRadius: "8px",
    background: "var(--Brand-50, #E7FFE4)",
    padding: "6px 8px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  currencySelectSubMenu: {
    borderTopRightRadius: "0 !important",
    borderBottomRightRadius: "0 !important",
    borderTop: "none !important",
    borderBottom: "none !important",
    borderLeft: "none !important",
    borderRight: "1px solid var(--Gray-300, #D0D5DD)",
    paddingRight: "0",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      border: "none !important",
    },
    "& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input": {
      padding: "8px 16px 8px 8px",
    },
  },
  currencySelectSubMenuContainer: {
    display: "flex",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    borderRadius: "33px",
    width: "100%",
  },
  averageCostTextField: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-root": {
      border: "none !important",
    },
  },
  unitCostSubContainer: {
    width: "100%",
    display: "flex",
    gap: "16px",
  },
  unitCostSubItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
};
